import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import AuthLayout from '../../layout/AuthLayout.jsx';
import FormContainer from '../../components/form/FormContainer.jsx';
import Button from '../../components/Button.jsx';
import LoadingComponent from '../../components/LoadingComponent.jsx';
import FormError from '../../components/form/FormError.jsx';
import IdentityImage from '../../components/IdentityImage.jsx';
import {MCPOAuthApi} from '../../api/authority/generated/index.ts';
import {GlobalContext} from '../../utils/context/GlobalContextProvider.jsx';
import {getMcpAuthorizationHandle, requestMcpConsent} from './mcpConsent.js';
import {useTranslation} from 'react-i18next';

export function getMcpConsentProjectSummary(detail) {
    const names = Array.isArray(detail?.currentEligibleProjectNames) ? detail.currentEligibleProjectNames : [];
    const count = Number.isInteger(detail?.currentEligibleProjectCount) && detail.currentEligibleProjectCount >= 0 ? detail.currentEligibleProjectCount : 0;
    const remaining = Number.isInteger(detail?.remainingEligibleProjectCount) && detail.remainingEligibleProjectCount >= 0 ? detail.remainingEligibleProjectCount : Math.max(0, count - names.length);
    return {names, count, remaining};
}

export function getMcpConsentIdentitySummary(identity) {
    const firstName = typeof identity?.user?.firstName === 'string' ? identity.user.firstName.trim() : '';
    const lastName = typeof identity?.user?.lastName === 'string' ? identity.user.lastName.trim() : '';
    const email = typeof identity?.user?.email === 'string' ? identity.user.email.trim() : '';
    return {name: [firstName, lastName].filter(Boolean).join(' '), email};
}

export function switchMcpConsentAccount({handle, setCurrentIdentity, replace = (url) => window.location.replace(url)}) {
    if (getMcpAuthorizationHandle(`?request=${handle}`) === null) return false;

    const returnTo = `/oauth/authorize?request=${handle}`;
    setCurrentIdentity(null);
    replace(`/sign/in?returnTo=${encodeURIComponent(returnTo)}`);
    return true;
}

export function getMcpConsentErrorMessage(error, t) {
    switch (error?.message) {
        case 'invalid_request':
            return t('mcp_oauth.consent.error-expired', {defaultValue: 'Autorizační požadavek vypršel nebo už byl použit. Vraťte se do AI klienta a spusťte připojení znovu.'});
        case 'access_denied':
            return t('mcp_oauth.consent.error-denied', {defaultValue: 'Připojení bylo zamítnuto. Nový pokus můžete spustit v AI klientovi.'});
        default:
            return t('mcp_oauth.consent.error-fallback', {defaultValue: 'Připojení se nepodařilo dokončit. Spusťte autorizaci znovu v AI klientovi; pokud problém přetrvá, kontaktujte podporu.'});
    }
}

export function McpConsentError({error, t, footer = null}) {
    return <FormError
        error={error}
        message={getMcpConsentErrorMessage(error, t)}
        title={t('mcp_oauth.consent.error-title', {defaultValue: 'Připojení se nezdařilo'})}
        variant="card"
        footer={footer}
    />;
}

export function McpConsentContent({detail, identity, onApprove, onDeny, onSwitchAccount, busy, t}) {
    const {names, count, remaining} = getMcpConsentProjectSummary(detail);
    const {name, email} = getMcpConsentIdentitySummary(identity);
    const superadmin = detail.accessMode === 'superadmin';

    return <div className="overflow-hidden app-surface rounded-3xl border border-white/15 bg-white shadow-2xl shadow-slate-950/40">
        <div className="border-b border-slate-200 bg-gradient-to-br from-white via-white to-violet-50 px-5 py-6 sm:px-8 sm:py-7">
            <p className="text-xs font-bold uppercase tracking-[0.18em] text-violet-600">
                {t('mcp_oauth.consent.eyebrow', {defaultValue: 'Bezpečné připojení'})}
            </p>
            <p className="mt-2 max-w-xl text-base leading-6 text-slate-600">
                {t('mcp_oauth.consent.request-prefix', {defaultValue: 'Aplikace'})}{' '}
                <strong className="font-bold text-slate-950">{detail.clientName}</strong>{' '}
                {t('mcp_oauth.consent.request-suffix', {defaultValue: 'žádá o přístup pouze ke čtení reportů Datixo.'})}
            </p>
        </div>
        <FormContainer>
            <div className="space-y-5 px-5 py-6 sm:px-8 sm:py-7">
                <div>
                    <h2 id="mcp-consent-current-account" className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">
                        {t('mcp_oauth.consent.account-heading', {defaultValue: 'Přihlášený účet'})}
                    </h2>
                    <section aria-labelledby="mcp-consent-current-account" className="app-surface flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4 text-sm text-slate-700 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                        <div className="flex min-w-0 items-center gap-3">
                            <IdentityImage medium url={identity?.user?.avatarUrl} user={identity?.user}/>
                            <div className="min-w-0 leading-5">
                                {name && <p className="font-semibold text-slate-950">{name}</p>}
                                {email && <p className={`${name ? '' : 'font-semibold text-slate-950'} break-all text-slate-600`}>{email}</p>}
                            </div>
                        </div>
                    </div>
                    <Button className="shrink-0 self-start sm:self-auto" variant="outline-violet" size="small" disabled={busy} onClick={onSwitchAccount}>
                        {t('mcp_oauth.consent.switch-account', {defaultValue: 'Použít jiný účet'})}
                    </Button>
                    </section>
                </div>
                {superadmin ? <div role="alert" className="app-surface rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm leading-6 text-amber-950">
                    {t('mcp_oauth.consent.superadmin-scope', {defaultValue: 'Jako superadministrátor povolujete přístup ke všem projektům, včetně projektů bez aktivního MCP balíčku. Rozsah se řídí vaším aktuálním účtem.'})}
                </div> : <div className="app-surface rounded-2xl border border-violet-100 bg-violet-50 px-4 py-4 text-sm leading-6 text-slate-700">
                    <p>{t('mcp_oauth.consent.account-scope', {count, defaultValue: 'Připojení získá přístup ke všem vašim současným projektům s aktivním MCP v balíčku (aktuálně {{count}}) a automaticky i k budoucím způsobilým projektům.'})}</p>
                    {names.length > 0 && <p className="mt-2 font-semibold text-slate-950">{names.join(', ')}{remaining > 0 ? t('mcp_oauth.consent.remaining-projects', {count: remaining, defaultValue: ' a dalších {{count}}'}) : ''}</p>}
                    {count === 0 && <p role="status" className="mt-2">{t('mcp_oauth.consent.zero-projects', {defaultValue: 'Momentálně nemáte žádný způsobilý projekt. Připojení lze přesto povolit a začne fungovat, jakmile některý projekt podmínky splní.'})}</p>}
                </div>}
                <div className="space-y-2 border-t border-slate-200 pt-5 text-xs leading-5 text-slate-500">
                    <p>{t('mcp_oauth.consent.read-only-notice', {defaultValue: 'Přístup dovoluje pouze předem definované read-only reporty. Neumožňuje zápis ani přímý přístup do databáze.'})}</p>
                    <p>{t('mcp_oauth.consent.revocation-scope', {defaultValue: 'Odpojením později odeberete tomuto AI klientovi přístup ke všem projektům najednou.'})}</p>
                </div>
            </div>
            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-5 sm:flex-row sm:items-center sm:justify-end sm:px-8">
                <Button className="w-full justify-center sm:w-auto" variant="outline-red" disabled={busy} onClick={onDeny}>
                    {t('mcp_oauth.consent.deny', {defaultValue: 'Zamítnout'})}
                </Button>
                <Button className="w-full justify-center sm:w-auto" variant="violet" disabled={busy} onClick={onApprove}>
                    {t('mcp_oauth.consent.approve', {defaultValue: 'Povolit připojení'})}
                </Button>
            </div>
        </FormContainer>
    </div>;
}

export default function McpAuthorizationConsent() {
    const {t} = useTranslation();
    const {context, getAuthorityConfiguration, setCurrentIdentity} = useContext(GlobalContext);
    const location = useLocation();
    const handle = useMemo(() => getMcpAuthorizationHandle(location.search), [location.search]);
    const [detailState, setDetailState] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);
    const requestEpochRef = useRef(0);
    const token = context.currentIdentity?.token ?? null;
    const detail = detailState?.handle === handle ? detailState.value : null;

    useEffect(() => {
        if (!token || handle === null) return;
        let active = true;
        const requestEpoch = ++requestEpochRef.current;
        setDetailState(null);
        setError(null);
        setBusy(true);
        requestMcpConsent({api: new MCPOAuthApi(getAuthorityConfiguration()), handle})
            .then((value) => { if (active && requestEpochRef.current === requestEpoch) setDetailState({handle, value}); })
            .catch((requestError) => { if (active && requestEpochRef.current === requestEpoch) setError(requestError); })
            .finally(() => { if (active && requestEpochRef.current === requestEpoch) setBusy(false); });
        return () => { active = false; };
    }, [getAuthorityConfiguration, handle, refreshKey, token]);

    if (handle === null) {
        return <AuthLayout title={t('mcp_oauth.consent.title', {defaultValue: 'Připojení AI'})}>
            <McpConsentError error={new Error('invalid_request')} t={t}/>
        </AuthLayout>;
    }

    if (!token) {
        const returnTo = `${location.pathname}${location.search}`;
        return <AuthLayout title={t('mcp_oauth.consent.title', {defaultValue: 'Připojení AI'})}>
            <FormContainer>
                <p className="mb-4 text-sm text-slate-600">
                    {t('mcp_oauth.consent.sign-in-required', {defaultValue: 'Pro povolení připojení se nejprve přihlaste do Datixo.'})}
                </p>
                <Link className="btn bg-gray-900 text-gray-100" to={`/sign/in?returnTo=${encodeURIComponent(returnTo)}`}>
                    {t('mcp_oauth.consent.sign-in', {defaultValue: 'Přihlásit se'})}
                </Link>
            </FormContainer>
        </AuthLayout>;
    }

    const finish = async (action) => {
        setBusy(true);
        setError(null);
        try {
            const result = await requestMcpConsent({
                api: new MCPOAuthApi(getAuthorityConfiguration()),
                handle,
                action,
            });
            window.location.assign(result.redirectUri);
        } catch (requestError) {
            setError(requestError);
            setBusy(false);
        }
    };

    const switchAccount = () => {
        requestEpochRef.current += 1;
        setBusy(true);
        setError(null);
        switchMcpConsentAccount({handle, setCurrentIdentity});
    };

    return <AuthLayout title={t('mcp_oauth.consent.title', {defaultValue: 'Připojení AI'})} contentClassName="max-w-2xl">
        {busy && detail === null && <p className="sr-only" role="status" aria-live="polite">
            {t('mcp_oauth.consent.loading', {defaultValue: 'Načítám autorizační požadavek…'})}
        </p>}
        <LoadingComponent loading={busy && detail === null}>
            {error && <McpConsentError error={error} t={t} footer={detail === null ? <>
                <Link className="btn w-full justify-center border border-gray-300 bg-white text-gray-700 sm:w-auto" to="/">
                    {t('mcp_oauth.consent.back', {defaultValue: 'Zpět do Datixo'})}
                </Link>
                {error.message !== 'invalid_request' && <Button className="w-full justify-center sm:w-auto" variant="violet" disabled={busy} onClick={() => setRefreshKey((key) => key + 1)}>
                    {t('mcp_oauth.consent.retry', {defaultValue: 'Zkusit znovu'})}
                </Button>}
            </> : null}/>}
            {detail && <McpConsentContent
                detail={detail}
                identity={context.currentIdentity}
                onApprove={() => void finish('approve')}
                onDeny={() => void finish('deny')}
                onSwitchAccount={switchAccount}
                busy={busy}
                t={t}
            />}
        </LoadingComponent>
    </AuthLayout>;
}
